/**
 * @fileoverview Room Detail Sidebar Drawer Component
 * @author Hospital Room Scheduler Team
 * @created 08-10-2025 02:16:29
 * 
 * Comprehensive room information sidebar that displays:
 * - Room basic info (number, name, capacity, status, specialties)
 * - Connected data: Assignments, Session Times, Blocks, Conflicts
 * - Utilization statistics and analytics
 * 
 * Opened when user clicks room in BuildingTreeView, controlled via URL (?roomId=uuid)
 * Uses newly created room management hooks for data fetching
 */

import React, { useState } from 'react';
import {
  Drawer,
  Stack,
  Group,
  Text,
  Badge,
  Tabs,
  Button,
  Card,
  Table,
  ScrollArea,
  Alert,
  Loader,
  Center,
  ActionIcon,
  Box,
  Divider,
  SimpleGrid,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconBuilding,
  IconStairs,
  IconDoor,
  IconUsers,
  IconCalendar,
  IconClock,
  IconBan,
  IconAlertTriangle,
  IconRefresh,
  IconInfoCircle,
  IconCheck,
  IconEdit,
  IconPlus,
} from '@tabler/icons-react';
import { useRoom, useRoomSchedule, useRoomSessionTimes, useRoomBlocks, useRoomConflicts, useUpdateRoom, roomQueryKeys } from '../../hooks/admin/useRoomManagement';
import { useQueryClient } from '@tanstack/react-query';
import { RoomForm } from '../forms/RoomForm';
import AssignModal from '../modals/AssignModal';
import BlockModal from '../modals/BlockModal';
import { SessionTimeForm } from '../forms/SessionTimeForm';
import { useUsers } from '../../hooks/admin/useUsers';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

/**
 * Props for RoomDetailSidebar component
 */
export interface RoomDetailSidebarProps {
  /** Room ID to display (UUID) - null when no room selected */
  roomId: string | null;
  
  /** Control drawer open state */
  opened: boolean;
  
  /** Callback when drawer closes */
  onClose: () => void;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Room Detail Sidebar Component
 * 
 * Displays comprehensive room information in a right-side drawer with tabbed interface.
 * Shows all data connected to room from database: assignments, session times, blocks, conflicts.
 * 
 * Features:
 * - 5 tabs: Overview, Schedule, Sessions, Blocks, Conflicts
 * - Real-time data from backend APIs via React Query hooks
 * - Loading states with skeleton loaders
 * - Error handling with retry buttons
 * - Empty states for no data scenarios
 * - Responsive design (full screen on mobile)
 * - Keyboard accessible (Tab, Esc to close)
 * 
 * @component
 * @example
 * ```tsx
 * <RoomDetailSidebar
 *   roomId={selectedRoomId}
 *   opened={!!selectedRoomId}
 *   onClose={() => setSelectedRoomId(null)}
 * />
 * ```
 */
export function RoomDetailSidebar({
  roomId,
  opened,
  onClose,
}: RoomDetailSidebarProps): React.ReactElement {
  
  // =============================================================================
  // STATE
  // =============================================================================
  
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [showEditRoomModal, setShowEditRoomModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  
  // Date range for schedule/blocks/conflicts (default: next 30 days)
  const [dateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });
  
  const queryClient = useQueryClient();
  
  // =============================================================================
  // DATA FETCHING
  // =============================================================================
  
  // Fetch room basic data
  const {
    data: room,
    isLoading: isLoadingRoom,
    error: roomError,
    refetch: refetchRoom,
  } = useRoom(roomId || '');
  
  
  // Fetch doctors for assignment modal
  const { data: doctorsData } = useUsers({ role: 'DOCTOR', limit: 100 });
  
  // Update room mutation
  const updateRoomMutation = useUpdateRoom();
  
  // Fetch schedule data (assignments)
  const {
    data: schedule,
    isLoading: isLoadingSchedule,
    error: scheduleError,
    refetch: refetchSchedule,
  } = useRoomSchedule(
    roomId || '',
    dateRange.startDate,
    dateRange.endDate
  );
  
  // Fetch session times
  const {
    data: sessionTimes,
    isLoading: isLoadingSessionTimes,
    error: sessionTimesError,
    refetch: refetchSessionTimes,
  } = useRoomSessionTimes(roomId || '');
  
  // Fetch blocks
  const {
    data: blocks,
    isLoading: isLoadingBlocks,
    error: blocksError,
    refetch: refetchBlocks,
  } = useRoomBlocks(
    roomId || '',
    dateRange.startDate,
    dateRange.endDate
  );
  
  // Fetch conflicts
  const {
    data: conflicts,
    isLoading: isLoadingConflicts,
    error: conflictsError,
    refetch: refetchConflicts,
  } = useRoomConflicts(
    roomId || '',
    dateRange.startDate,
    dateRange.endDate
  );
  
  // =============================================================================
  // RENDER HELPERS
  // =============================================================================
  
  /**
   * Render loading state for drawer content
   */
  const renderLoading = () => (
    <Center h={400}>
      <Stack align="center" gap="md">
        <Loader size="lg" />
        <Text c="dimmed">Loading room details...</Text>
      </Stack>
    </Center>
  );
  
  /**
   * Render error state with retry button
   */
  const renderError = (error: any, onRetry: () => void) => (
    <Alert
      icon={<IconAlertTriangle size={16} />}
      title="Error Loading Data"
      color="red"
      variant="light"
    >
      <Stack gap="sm">
        <Text size="sm">{error?.message || 'Failed to load data. Please try again.'}</Text>
        <Button size="sm" variant="light" onClick={onRetry} leftSection={<IconRefresh size={14} />}>
          Retry
        </Button>
      </Stack>
    </Alert>
  );
  
  // =============================================================================
  // TAB CONTENT COMPONENTS
  // =============================================================================
  
  /**
   * Overview Tab - Room basic info, capacity, specialties, quick stats
   */
  const OverviewTab = () => {
    if (isLoadingRoom) return renderLoading();
    if (roomError) return renderError(roomError, refetchRoom);
    if (!room) return <Text c="dimmed">No room data available</Text>;
    
    return (
      <Stack gap="md">
        {/* Room Information Card */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={600} size="sm" c="dimmed">ROOM INFORMATION</Text>
              <Tooltip label="Edit Room">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => setShowEditRoomModal(true)}
                >
                  <IconEdit size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            
            <Divider />
            
            {/* Room Number & Name */}
            <Group gap="xs">
              <ThemeIcon size="md" radius="md" variant="light" color="blue">
                <IconDoor size={16} />
              </ThemeIcon>
              <Box style={{ flex: 1 }}>
                <Text size="xs" c="dimmed">Room Number</Text>
                <Text fw={600} size="lg">{room.number}</Text>
              </Box>
            </Group>
            
            <Box>
              <Text size="xs" c="dimmed">Room Name</Text>
              <Text fw={500}>{room.name}</Text>
            </Box>
            
            {/* Location */}
            <Box>
              <Text size="xs" c="dimmed">Location</Text>
              <Group gap="xs">
                <IconBuilding size={14} />
                <Text size="sm">{room.floor.building.name}</Text>
                <Text size="sm" c="dimmed">•</Text>
                <IconStairs size={14} />
                <Text size="sm">{room.floor.name}</Text>
              </Group>
            </Box>
            
            {/* Capacity */}
            <Group gap="md">
              <Tooltip label="Maximum number of people this room can accommodate">
                <Box style={{ flex: 1 }}>
                  <Text size="xs" c="dimmed">Capacity</Text>
                  <Group gap="xs">
                    <IconUsers size={16} />
                    <Text fw={600} size="lg">{room.capacity}</Text>
                    <Text size="sm" c="dimmed">persons</Text>
                  </Group>
                </Box>
              </Tooltip>
              
              {/* Status Badge */}
              <Tooltip label={room.isActive ? 'Room is available for scheduling' : 'Room is inactive and cannot be scheduled'}>
                <Badge
                  size="lg"
                  color={room.isActive ? 'green' : 'gray'}
                  variant="light"
                  leftSection={room.isActive ? <IconCheck size={14} /> : '○'}
                >
                  {room.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Tooltip>
            </Group>
            
            {/* Description */}
            {room.description && (
              <Box>
                <Text size="xs" c="dimmed">Description</Text>
                <Text size="sm">{room.description}</Text>
              </Box>
            )}
          </Stack>
        </Card>
        
        {/* Specialty Tags Card */}
        {(room as any).allowedSpecialties && (room as any).allowedSpecialties.length > 0 && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="sm">
              <Text fw={600} size="sm" c="dimmed">ALLOWED SPECIALTIES</Text>
              <Divider />
              <Group gap="xs">
                {(room as any).allowedSpecialties.map((specialty: string) => (
                  <Badge key={specialty} variant="light" color="blue">
                    {specialty.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </Group>
            </Stack>
          </Card>
        )}
        
        {/* Quick Stats */}
        <SimpleGrid cols={2} spacing="md">
          <Card shadow="sm" padding="md" radius="md" withBorder>
            <Stack gap="xs" align="center">
              <ThemeIcon size="xl" radius="md" variant="light" color="blue">
                <IconClock size={24} />
              </ThemeIcon>
              <Text size="xl" fw={700}>{sessionTimes?.length || 0}</Text>
              <Text size="xs" c="dimmed" ta="center">Session Times</Text>
            </Stack>
          </Card>
          
          <Card shadow="sm" padding="md" radius="md" withBorder>
            <Stack gap="xs" align="center">
              <ThemeIcon size="xl" radius="md" variant="light" color="orange">
                <IconBan size={24} />
              </ThemeIcon>
              <Text size="xl" fw={700}>{blocks?.length || 0}</Text>
              <Text size="xs" c="dimmed" ta="center">Active Blocks</Text>
            </Stack>
          </Card>
        </SimpleGrid>
        
        {/* Conflicts Warning */}
        {conflicts && conflicts.length > 0 && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Conflicts Detected"
            color="red"
            variant="light"
          >
            <Stack gap="xs">
              <Text size="sm">
                This room has {conflicts.length} scheduling conflict{conflicts.length !== 1 ? 's' : ''} that need attention.
              </Text>
              <Button
                size="xs"
                variant="light"
                color="red"
                onClick={() => setActiveTab('conflicts')}
              >
                View Conflicts
              </Button>
            </Stack>
          </Alert>
        )}
      </Stack>
    );
  };
  
  /**
   * Schedule Tab - Upcoming assignments for this room
   */
  const ScheduleTab = () => {
    if (isLoadingSchedule) return renderLoading();
    if (scheduleError) return renderError(scheduleError, refetchSchedule);
    
    if (!schedule || schedule.length === 0) {
      return (
        <Box p="xl" style={{ textAlign: 'center' }}>
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="md" variant="light" color="gray">
              <IconCalendar size={30} />
            </ThemeIcon>
            <Text size="lg" fw={500} c="dimmed">No assignments scheduled</Text>
            <Text size="sm" c="dimmed">
              This room has no upcoming assignments in the next 30 days
            </Text>
            <Text size="xs" c="dimmed" ta="center" style={{ maxWidth: 300 }}>
              Assignments are created when doctors are scheduled to use this room for specific session times
            </Text>
            <Tooltip label="Assign a doctor to this room for a specific session time" position="bottom">
              <Button
                size="sm"
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={() => setShowAssignModal(true)}
              >
                Create Assignment
              </Button>
            </Tooltip>
          </Stack>
        </Box>
      );
    }
    
    return (
      <Stack gap="md">
        <Group justify="space-between">
          <Stack gap={2}>
            <Text fw={600} size="sm" c="dimmed">
              UPCOMING ASSIGNMENTS ({schedule.length})
            </Text>
            <Text size="xs" c="dimmed">
              Assignments show when doctors are scheduled to use this room for specific session times
            </Text>
          </Stack>
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={() => refetchSchedule()}>
            Refresh
          </Button>
        </Group>
        
        <ScrollArea h={500}>
          <Stack gap="xs">
            {schedule.map((assignment: any) => (
              <Card key={assignment.id} shadow="sm" padding="md" radius="md" withBorder>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Tooltip label="Assignment title and description">
                      <Text fw={600}>{assignment.title || 'Untitled Assignment'}</Text>
                    </Tooltip>
                    <Tooltip 
                      label={
                        assignment.status === 'CONFIRMED' 
                          ? 'Assignment is confirmed and active'
                          : assignment.status === 'PENDING'
                          ? 'Assignment is pending approval or confirmation'
                          : assignment.status === 'CANCELLED'
                          ? 'Assignment has been cancelled'
                          : 'Assignment status is unknown'
                      }
                    >
                      <Badge
                        size="sm"
                        color={
                          assignment.status === 'CONFIRMED' ? 'green' :
                          assignment.status === 'PENDING' ? 'yellow' :
                          assignment.status === 'CANCELLED' ? 'red' : 'gray'
                        }
                      >
                        {assignment.status}
                      </Badge>
                    </Tooltip>
                  </Group>
                  
                  <Group gap="xs">
                    <IconUsers size={14} />
                    <Tooltip label="Assigned doctor for this session">
                      <Text size="sm">
                        {assignment.users?.name || assignment.user?.name || assignment.doctor?.name || 'Unassigned'}
                      </Text>
                    </Tooltip>
                    {(assignment.users?.specialty || assignment.user?.specialty) && (
                      <Tooltip label="Doctor's medical specialty">
                        <Badge size="xs" variant="light">
                          {assignment.users?.specialty || assignment.user?.specialty}
                        </Badge>
                      </Tooltip>
                    )}
                  </Group>
                  
                  <Group gap="xs">
                    <IconCalendar size={14} />
                    <Tooltip label="Assignment date">
                      <Text size="sm">{new Date(assignment.date).toLocaleDateString()}</Text>
                    </Tooltip>
                    <Text size="sm" c="dimmed">•</Text>
                    <IconClock size={14} />
                    <Tooltip label="Session time period">
                      <Text size="sm">
                        {assignment.session_times?.startTime && assignment.session_times?.endTime
                          ? `${assignment.session_times.startTime} - ${assignment.session_times.endTime}`
                          : assignment.sessionTime?.name || assignment.session_times?.name || 'N/A'
                        }
                      </Text>
                    </Tooltip>
                  </Group>
                  
                  {assignment.description && (
                    <Text size="xs" c="dimmed" lineClamp={2}>{assignment.description}</Text>
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
        </ScrollArea>
      </Stack>
    );
  };
  
  /**
   * Session Times Tab - Configured session times for this room
   */
  const SessionTimesTab = () => {
    if (isLoadingSessionTimes) return renderLoading();
    if (sessionTimesError) return renderError(sessionTimesError, refetchSessionTimes);
    
    if (!sessionTimes || sessionTimes.length === 0) {
      return (
        <Box p="xl" style={{ textAlign: 'center' }}>
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="md" variant="light" color="gray">
              <IconClock size={30} />
            </ThemeIcon>
            <Text size="lg" fw={500} c="dimmed">No session times configured</Text>
            <Text size="sm" c="dimmed">
              Define session times to enable room scheduling
            </Text>
            <Button
              size="sm"
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={() => setShowSessionModal(true)}
            >
              Add Session Time
            </Button>
          </Stack>
        </Box>
      );
    }
    
    return (
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600} size="sm" c="dimmed">
            CONFIGURED SESSION TIMES ({sessionTimes.length})
          </Text>
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={() => refetchSessionTimes()}>
            Refresh
          </Button>
        </Group>
        
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Tooltip label="Session time name and identifier">
                  <Text>Session Name</Text>
                </Tooltip>
              </Table.Th>
              <Table.Th>
                <Tooltip label="Session start time (24-hour format)">
                  <Text>Start Time</Text>
                </Tooltip>
              </Table.Th>
              <Table.Th>
                <Tooltip label="Session end time (24-hour format)">
                  <Text>End Time</Text>
                </Tooltip>
              </Table.Th>
              <Table.Th>
                <Tooltip label="Buffer time in minutes before and after session">
                  <Text>Buffer (min)</Text>
                </Tooltip>
              </Table.Th>
              <Table.Th>
                <Tooltip label="Whether this session time is active and available for booking">
                  <Text>Status</Text>
                </Tooltip>
              </Table.Th>
              <Table.Th>
                <Tooltip label="When this session time was created and last updated">
                  <Text>Created/Updated</Text>
                </Tooltip>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sessionTimes.map((session: any) => (
              <Table.Tr key={session.id}>
                <Table.Td>
                  <Text fw={500}>{session.name}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <IconClock size={14} />
                    <Text size="sm">{session.startTime}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <IconClock size={14} />
                    <Text size="sm">{session.endTime}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{session.bufferMinutes || 0} min</Text>
                </Table.Td>
                <Table.Td>
                  <Badge
                    size="sm"
                    color={session.isActive ? 'green' : 'gray'}
                    variant="light"
                  >
                    {session.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Tooltip label="Created on">
                      <Text size="xs" c="dimmed">
                        Created: {new Date(session.createdAt).toLocaleDateString()}
                      </Text>
                    </Tooltip>
                    <Tooltip label="Last updated on">
                      <Text size="xs" c="dimmed">
                        Updated: {new Date(session.updatedAt).toLocaleDateString()}
                      </Text>
                    </Tooltip>
                  </Stack>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    );
  };
  
  /**
   * Blocks Tab - Maintenance and cleaning blocks
   */
  const BlocksTab = () => {
    if (isLoadingBlocks) return renderLoading();
    if (blocksError) return renderError(blocksError, refetchBlocks);
    
    if (!blocks || blocks.length === 0) {
      return (
        <Box p="xl" style={{ textAlign: 'center' }}>
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="md" variant="light" color="green">
              <IconCheck size={30} />
            </ThemeIcon>
            <Text size="lg" fw={500} c="dimmed">No maintenance blocks</Text>
            <Text size="sm" c="dimmed">
              Room is fully available for scheduling
            </Text>
            <Text size="xs" c="dimmed" ta="center" style={{ maxWidth: 300 }}>
              Blocks are used to mark periods when the room is unavailable (maintenance, cleaning, etc.)
            </Text>
            <Tooltip label="Block this room for maintenance, cleaning, or other unavailable periods" position="bottom">
              <Button
                size="sm"
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={() => setShowBlockModal(true)}
              >
                Add Block (Optional)
              </Button>
            </Tooltip>
          </Stack>
        </Box>
      );
    }
    
    return (
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600} size="sm" c="dimmed">
            MAINTENANCE BLOCKS ({blocks.length})
          </Text>
          <Group gap="xs">
            <Tooltip label="Block this room for maintenance, cleaning, or other unavailable periods" position="bottom">
              <Button 
                size="xs" 
                variant="light" 
                leftSection={<IconPlus size={14} />}
                onClick={() => setShowBlockModal(true)}
              >
                Add Block
              </Button>
            </Tooltip>
            <Tooltip label="Refresh the blocks list to see latest changes" position="bottom">
              <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={() => refetchBlocks()}>
                Refresh
              </Button>
            </Tooltip>
          </Group>
        </Group>
        
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Tooltip label="Name and description of the maintenance block">
                <Table.Th>Block Name</Table.Th>
              </Tooltip>
              <Tooltip label="Type of block (maintenance, cleaning, etc.)">
                <Table.Th>Type</Table.Th>
              </Tooltip>
              <Tooltip label="When the block period starts">
                <Table.Th>Start Date</Table.Th>
              </Tooltip>
              <Tooltip label="When the block period ends">
                <Table.Th>End Date</Table.Th>
              </Tooltip>
              <Tooltip label="Whether the block is currently active or inactive">
                <Table.Th>Status</Table.Th>
              </Tooltip>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {blocks.map((block: any) => (
              <Table.Tr key={block.id}>
                <Table.Td>
                  <Text fw={500}>{block.name}</Text>
                  {block.note && (
                    <Text size="xs" c="dimmed" lineClamp={1}>{block.note}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color="orange">
                    {block.type}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{new Date(block.startDate).toLocaleDateString()}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{new Date(block.endDate).toLocaleDateString()}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge
                    size="sm"
                    color={block.isActive ? 'orange' : 'gray'}
                    variant="light"
                  >
                    {block.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    );
  };
  
  /**
   * Conflicts Tab - Detected scheduling conflicts
   */
  const ConflictsTab = () => {
    if (isLoadingConflicts) return renderLoading();
    if (conflictsError) return renderError(conflictsError, refetchConflicts);
    
    if (!conflicts || conflicts.length === 0) {
      return (
        <Box p="xl" style={{ textAlign: 'center' }}>
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="md" variant="light" color="green">
              <IconCheck size={30} />
            </ThemeIcon>
            <Text size="lg" fw={500} c="dimmed">No conflicts detected</Text>
            <Text size="sm" c="dimmed">
              All assignments for this room are valid and conflict-free
            </Text>
            <Text size="xs" c="dimmed" ta="center" style={{ maxWidth: 300 }}>
              Conflicts occur when assignments violate scheduling rules (double booking, capacity exceeded, etc.)
            </Text>
          </Stack>
        </Box>
      );
    }
    
    return (
      <Stack gap="md">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Conflicts Require Attention"
          color="red"
          variant="light"
        >
          Found {conflicts.length} scheduling conflict{conflicts.length !== 1 ? 's' : ''} for this room
        </Alert>
        
        <Stack gap="xs">
          {conflicts.map((conflict: any, index: number) => (
            <Card key={index} shadow="sm" padding="md" radius="md" withBorder>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Badge size="sm" color="red" variant="filled">
                    {conflict.code || 'CONFLICT'}
                  </Badge>
                  <Text size="xs" c="dimmed">{new Date(conflict.date).toLocaleDateString()}</Text>
                </Group>
                
                <Text fw={500} size="sm">{conflict.message}</Text>
                
                {conflict.suggestion && (
                  <Box p="sm" style={{ backgroundColor: 'var(--mantine-color-yellow-0)', borderRadius: 'var(--mantine-radius-sm)' }}>
                    <Group gap="xs">
                      <IconInfoCircle size={14} />
                      <Text size="xs" fw={500}>Suggestion:</Text>
                    </Group>
                    <Text size="xs" c="dimmed">{conflict.suggestion}</Text>
                  </Box>
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      </Stack>
    );
  };
  
  // =============================================================================
  // RENDER DRAWER
  // =============================================================================
  
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        room ? (
          <Group gap="md">
            <ThemeIcon size="lg" radius="md" variant="light" color="blue">
              <IconDoor size={24} />
            </ThemeIcon>
            <Box>
              <Text fw={700} size="lg">Room {room?.number || roomId}</Text>
              <Text size="sm" c="dimmed">{room?.name || 'Loading...'}</Text>
            </Box>
          </Group>
        ) : (
          <Text fw={700} size="lg">Room Details</Text>
        )
      }
      scrollAreaComponent={ScrollArea.Autosize}
      styles={{
        header: {
          paddingBottom: '1rem',
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        },
        body: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'overview')} style={{ flex: 1 }}>
        <Tabs.List>
          <Tooltip label="View room details, capacity, and specifications" position="bottom">
            <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={14} />}>
              Overview
            </Tabs.Tab>
          </Tooltip>
          <Tooltip label="View and manage room assignments and sessions" position="bottom">
            <Tabs.Tab value="schedule" leftSection={<IconCalendar size={14} />}>
              Schedule
            </Tabs.Tab>
          </Tooltip>
          <Tooltip label="View and manage session times for this room" position="bottom">
            <Tabs.Tab value="sessions" leftSection={<IconClock size={14} />}>
              Sessions
            </Tabs.Tab>
          </Tooltip>
          <Tooltip label="View maintenance blocks and unavailable periods" position="bottom">
            <Tabs.Tab value="blocks" leftSection={<IconBan size={14} />}>
              Blocks
              {blocks && blocks.length > 0 && (
                <Badge size="xs" circle ml={4}>{blocks.length}</Badge>
              )}
            </Tabs.Tab>
          </Tooltip>
          <Tooltip label="View scheduling conflicts and failed assignments" position="bottom">
            <Tabs.Tab value="conflicts" leftSection={<IconAlertTriangle size={14} />}>
              Conflicts
              {conflicts && conflicts.length > 0 && (
                <Badge size="xs" color="red" circle ml={4}>{conflicts.length}</Badge>
              )}
            </Tabs.Tab>
          </Tooltip>
        </Tabs.List>
        
        <Tabs.Panel value="overview" pt="md">
          <OverviewTab />
        </Tabs.Panel>
        
        <Tabs.Panel value="schedule" pt="md">
          <ScheduleTab />
        </Tabs.Panel>
        
        <Tabs.Panel value="sessions" pt="md">
          <SessionTimesTab />
        </Tabs.Panel>
        
        <Tabs.Panel value="blocks" pt="md">
          <BlocksTab />
        </Tabs.Panel>
        
        <Tabs.Panel value="conflicts" pt="md">
          <ConflictsTab />
        </Tabs.Panel>
      </Tabs>

      {/* Edit Room Modal */}
      {showEditRoomModal && room && (
        <RoomForm
          opened={showEditRoomModal}
          onClose={() => setShowEditRoomModal(false)}
          onSubmit={async (data) => {
            await updateRoomMutation.mutateAsync({ id: room.id, roomData: data });
            queryClient.invalidateQueries({ queryKey: roomQueryKeys.detail(room.id) });
            setShowEditRoomModal(false);
            refetchRoom();
          }}
          loading={updateRoomMutation.isPending}
          roomId={room.id}
        />
      )}

      {/* Create Assignment Modal */}
      {showAssignModal && room && (
        <AssignModal
          opened={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            refetchSchedule();
          }}
          room={{
            id: room.id,
            number: room.number,
            name: room.name,
            capacity: room.capacity,
            isActive: room.isActive,
          }}
          doctors={doctorsData?.users || []}
          sessionTime={sessionTimes?.[0]} // Use first session time as default
          date={new Date()} // Default to today
        />
      )}

      {/* Create Session Time Modal */}
      {showSessionModal && room && (
        <SessionTimeForm
          opened={showSessionModal}
          onClose={() => {
            setShowSessionModal(false);
            refetchSessionTimes();
          }}
          onSubmit={async (_sessionTime) => {
            setShowSessionModal(false);
            refetchSessionTimes();
          }}
          roomId={room.id}
        />
      )}

      {/* Create Block Modal */}
      <BlockModal
        opened={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        roomId={room?.id || ''}
        onSuccess={() => {
          refetchBlocks();
        }}
      />
    </Drawer>
  );
}

